import { useMemo } from 'react';
import GuardIcon from '@/assets/icons/guard-primary.svg';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Button, Input } from '@/shared/ui';
import { passwordValidation } from '@/shared/helpers/validations';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';

const initialValues = {
    password: ''
};

const validationSchema = Yup.object().shape({
    password: passwordValidation,
});

interface IProps {
    loading?: boolean;
    email: string;
    onSubmit(password: string): void;
    step?: 'set-password' | 'set-password-pre-verified';
}

const CreatePasswordPopup = ({ loading, email, onSubmit, step }: IProps) => {
    const formik = useFormikWrapper({
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            onSubmit(values.password)
        }
    });

    const headerAndSubHeader = useMemo(() => {
        if (step === 'set-password-pre-verified') {
            return {
                header: 'Now let’s set up your password',
                subHeader: 'Your email will now be your login. Create a secure password to continue.'
            };
        }

        return {
            header: 'Your email is all set, now let’s set up a password',
            subHeader: 'Your email will now be your login. Create a secure password to continue.'
        }
    }, [step]);


    return (
        <form data-testid="set-password-flow-password-set-password-form-container" className="flex flex-col items-center" onSubmit={formik.handleSubmit}>
            <img src={GuardIcon} alt="guard icon" className="w-[30px] md:w-[41px] mb-2.5" />

            <h3 data-testid="set-password-flow-password-set-password-title" className="text-[1.5625rem] md:text-[2.1875rem] font-agrandir font-normal leading-normal mb-4 text-center">{headerAndSubHeader.header}</h3>
            <p data-testid="set-password-flow-password-set-password-subtitle" className="text-center text-17 md:text-xl mb-4 font-dmSans">{headerAndSubHeader.subHeader}</p>

            <h3 data-testid="set-password-flow-password-set-password-email-text" className="w-full font-bold font-dmSansMedium text-17 md:text-21 mb-1">Email:</h3>
            <p data-testid="set-password-flow-password-set-password-relevant-email" className="w-full mb-4 md:mb-[1.5625rem] text-17 md:text-xl font-dmSans font-normal">{email}</p>

            <Input
                name="password"
                className="mb-4 md:mb-6"
                label="Create Password"
                value={formik.values.password}
                type="password"
                placeholder="•••••••••"
                required
                error={!!(formik.touched.password && formik.errors.password)}
                errorMessage={formik.errors.password}
                onChange={event => formik.setFieldValue('password', event.target.value)}
                labelClassName="font-dmSans"
                enableNaturalEnter
                testId="set-password-flow-password-input"
            />

            <Button
                loading={loading}
                type="submit"
                className="w-full"
                showEndIcon
                testId="set-password-flow-password-set-password-button"
                fontClassName='font-bold'
            >
                Set Password
            </Button>
        </form>
    );
}

export default CreatePasswordPopup;